/**
 * Bridges a stored storefront theme to the embeddable opener's own theme shape.
 *
 * The opener keeps a seven-color semantic API (`LootTheme` is a published SDK
 * surface, not a shadcn consumer), so each of its colors reads the page token
 * that plays the same role. Shared because two callers need the identical
 * mapping: the storefront, and the dashboard's box preview — which mounts the
 * same opener and would otherwise drift from it.
 *
 * Returned structurally rather than typed as `LootTheme`, so this private
 * package stays free of a dependency on the published one. Assignability is
 * checked where each app passes it in.
 */
import { STOREFRONT_RARITY_TIERS } from "./rarityTiers.js";
import { resolveStorefrontThemeVars } from "./storefrontThemeVars.js";
import type { StorefrontTokenKey } from "./storefrontTokens.js";

/** Opener color role → the page token that plays the same part. */
const OPENER_TOKENS = {
  background: "background",
  surface: "card",
  border: "border",
  text: "foreground",
  textMuted: "muted-foreground",
  accent: "primary",
  accentForeground: "primary-foreground",
} as const satisfies Record<string, StorefrontTokenKey>;

export type OpenerColorRole = keyof typeof OPENER_TOKENS;

/** The opener's seven color roles, in the order an editor lists them. */
export const OPENER_COLOR_ROLES = Object.keys(OPENER_TOKENS) as OpenerColorRole[];

export interface OpenerThemeLike {
  colors?: Partial<Record<OpenerColorRole, string>>;
  rarity?: Partial<Record<number, string>>;
  radius?: string;
  fontFamily?: string;
  /** The opener kind's own variables, hex colors keyed by variable. */
  kind?: Record<string, string>;
}

/** Longest a rarity override may be — these reach an inline style. */
const MAX_RARITY_VALUE_LENGTH = 30;

/**
 * The opener's theme for a stored storefront theme, or undefined when there is
 * nothing stored (which lets the opener keep its own default palette).
 *
 * Colors come from the same sanitized, complete token set the page uses, so the
 * opener never half-inherits a palette. Rarity overrides pass through in either
 * accepted form — the opener's `resolveTheme` normalizes hex and "r,g,b"
 * itself — with out-of-range keys and overlong values dropped.
 */
export function resolveOpenerTheme(
  theme?: { rarity?: Record<string, string>; radius?: string; fontFamily?: string } & {
    tokens?: Record<string, string>;
  },
): OpenerThemeLike | undefined {
  if (!theme) return undefined;
  const resolved = resolveStorefrontThemeVars(theme);

  const colors: Partial<Record<OpenerColorRole, string>> = {};
  for (const [role, token] of Object.entries(OPENER_TOKENS) as [
    OpenerColorRole,
    StorefrontTokenKey,
  ][]) {
    colors[role] = resolved.colors[`--${token}`];
  }

  const rarity: Partial<Record<number, string>> = {};
  // Walking the tier table rather than the stored keys is what keeps a tier the
  // contract doesn't define out of the opener's palette — the same allowlist
  // rule resolveStorefrontThemeVars applies to the color tokens. It matters
  // here because the tier list has shrunk: a theme stored against the old one
  // can still carry a key no renderer has a color for.
  for (const tier of STOREFRONT_RARITY_TIERS) {
    const value = theme.rarity?.[tier.key];
    if (typeof value === "string" && value.length <= MAX_RARITY_VALUE_LENGTH) {
      rarity[Number(tier.key)] = value;
    }
  }

  return {
    ...(Object.keys(colors).length ? { colors } : {}),
    ...(Object.keys(rarity).length ? { rarity } : {}),
    ...(resolved.radius ? { radius: resolved.radius } : {}),
    ...(resolved.fontFamily ? { fontFamily: resolved.fontFamily } : {}),
  };
}
